import { BondCalculatorService } from './bond-calculator.service';
import { Component, ViewEncapsulation, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { SettingsBox } from './settings-box/settings-box.model';
import * as moment from 'moment';
import { BondData, Subheader, CalculateArgsWithId, CalculateArgsWithoutId, DataArgs } from './bond-calculator.model';
import { VwdOption } from '@vwd/vwd-pilldown';
import { CalcBox } from './calc-box/calc-box.model';
import { ResultBox } from './result-box/result-box.model';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-bond-calculator',
  templateUrl: './bond-calculator.component.html',
  styleUrls: ['./bond-calculator.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class BondCalculatorComponent implements OnInit, OnDestroy {

  defaultResultBox: ResultBox;

  defaultSettings: SettingsBox;

  defaultCalcBox: CalcBox;

  bondData: BondData;

  subheaderObj: Subheader;

  instrumentId: number;

  resultView: string;

  isLoading: boolean;

  noDataFound: boolean;

  settingsError: boolean;

  private ngUnsubscribe = new Subject();

  constructor(
    private bondService: BondCalculatorService,
    private cdRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute
  ) {
    this.initDefault();
  }

  ngOnInit(): void {

    this.activatedRoute.queryParams.subscribe(params => {
      if (params && params.instrumentId) {
        this.instrumentChanged(params.instrumentId);
      }
    });
  }

  changeView(event: string) {
    this.resultView = event;
  }

  handleSettingsError(event) {
    this.settingsError = event;
  }

  resetInstrument() {
    this.instrumentId = null;
    this.noDataFound = false;
    this.bondData = new BondData(this.defaultSettings, this.defaultResultBox, null, this.defaultCalcBox);
  }

  getResult() {
    this.isLoading = true;
    this.noDataFound = false;

    let paramsToCalculate: CalculateArgsWithId | CalculateArgsWithoutId;

    if (this.instrumentId) {
      paramsToCalculate = new CalculateArgsWithId(
        moment(this.bondData.settingsBox.firstCouponDate).format('YYYY-MM-DD'),
        typeof this.bondData.settingsBox.interestRate === 'string'
          ? parseFloat(this.bondData.settingsBox.interestRate) : this.bondData.settingsBox.interestRate,
        moment(this.bondData.settingsBox.maturityDate).format('YYYY-MM-DD'),
        typeof this.bondData.settingsBox.redemptionPrice === 'string'
          ? parseFloat(this.bondData.settingsBox.redemptionPrice) : this.bondData.settingsBox.redemptionPrice,
        moment(this.bondData.settingsBox.valueDate).format('YYYY-MM-DD'),
        moment(this.bondData.settingsBox.issueDate).format('YYYY-MM-DD'),
        moment(this.bondData.settingsBox.lastCouponDate).format('YYYY-MM-DD'),
        this.bondData.calcBox.cleanPrice || 0,
        this.bondData.calcBox.expectedYield || 0,
        typeof this.bondData.settingsBox.taxRate === 'string'
          ? parseFloat(this.bondData.settingsBox.taxRate) : this.bondData.settingsBox.taxRate,
        typeof this.bondData.settingsBox.nominalVolume === 'string'
          ? parseFloat(this.bondData.settingsBox.nominalVolume) : this.bondData.settingsBox.nominalVolume,
        this.bondData.settingsBox.couponFrequency,
        this.bondData.settingsBox.dayCountConvention,
        this.bondData.settingsBox.calculationMethod,
        this.instrumentId
      );

    } else {
      paramsToCalculate = new CalculateArgsWithoutId(
        moment(this.bondData.settingsBox.firstCouponDate).format('YYYY-MM-DD'),
        typeof this.bondData.settingsBox.interestRate === 'string'
          ? parseFloat(this.bondData.settingsBox.interestRate) : this.bondData.settingsBox.interestRate,
        moment(this.bondData.settingsBox.maturityDate).format('YYYY-MM-DD'),
        typeof this.bondData.settingsBox.redemptionPrice === 'string'
          ? parseFloat(this.bondData.settingsBox.redemptionPrice) : this.bondData.settingsBox.redemptionPrice,
        moment(this.bondData.settingsBox.valueDate).format('YYYY-MM-DD'),
        moment(this.bondData.settingsBox.issueDate).format('YYYY-MM-DD'),
        moment(this.bondData.settingsBox.lastCouponDate).format('YYYY-MM-DD'),
        this.bondData.calcBox.cleanPrice || 0,
        this.bondData.calcBox.expectedYield || 0,
        typeof this.bondData.settingsBox.taxRate === 'string'
          ? parseFloat(this.bondData.settingsBox.taxRate) : this.bondData.settingsBox.taxRate,
        typeof this.bondData.settingsBox.nominalVolume === 'string'
          ? parseFloat(this.bondData.settingsBox.nominalVolume) : this.bondData.settingsBox.nominalVolume,
        this.bondData.settingsBox.couponFrequency,
        this.bondData.settingsBox.dayCountConvention,
        this.bondData.settingsBox.calculationMethod,
      );

    }

    this.bondService.simulateCalculation(paramsToCalculate)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.markForCheck();
        })
      ).subscribe((data: ResultBox) => {

        const currentResult = JSON.parse(JSON.stringify(this.bondData.resultBox));

        if (data) {
          currentResult.expectedYield = data.expectedYield;
          currentResult.grossYield = data.grossYield;
          currentResult.netYield = data.netYield;
          currentResult.accruedInterest = data.accruedInterest;
          currentResult.basisPointValue = data.basisPointValue;
          currentResult.convexity = data.convexity;
          currentResult.maturity = data.maturity;
          currentResult.accruedInterestDays = data.accruedInterestDays;
          currentResult.totalAccurateInterest = data.totalAccurateInterest;
          currentResult.cashflowItem = data.cashflowItem;
        }

        this.bondData.resultBox = currentResult;
        this.bondData.calcBox.cleanPrice = data.cleanPrice;
        this.bondData.calcBox.expectedYield = data.expectedYield;
        this.bondData = Object.assign({}, this.bondData);

      }, (e: Error) => {
        console.error('Bond - Calcualtor - Widget - getResult', e);
        this.noDataFound = true;
      });
  }

  calcResult(value: CalcBox) {

    this.isLoading = true;
    this.noDataFound = false;

    const paramsToCalculate: CalculateArgsWithoutId = new CalculateArgsWithoutId(
      moment(this.bondData.settingsBox.firstCouponDate).format('YYYY-MM-DD'),
      typeof this.bondData.settingsBox.interestRate === 'string'
        ? parseFloat(this.bondData.settingsBox.interestRate) : this.bondData.settingsBox.interestRate,
      moment(this.bondData.settingsBox.maturityDate).format('YYYY-MM-DD'),
      typeof this.bondData.settingsBox.redemptionPrice === 'string'
        ? parseFloat(this.bondData.settingsBox.redemptionPrice) : this.bondData.settingsBox.redemptionPrice,
      moment(this.bondData.settingsBox.valueDate).format('YYYY-MM-DD'),
      moment(this.bondData.settingsBox.issueDate).format('YYYY-MM-DD'),
      moment(this.bondData.settingsBox.lastCouponDate).format('YYYY-MM-DD'),
      value.cleanPrice || 0,
      value.expectedYield || 0,
      typeof this.bondData.settingsBox.taxRate === 'string'
        ? parseFloat(this.bondData.settingsBox.taxRate) : this.bondData.settingsBox.taxRate,
      typeof this.bondData.settingsBox.nominalVolume === 'string'
        ? parseFloat(this.bondData.settingsBox.nominalVolume) : this.bondData.settingsBox.nominalVolume,
      this.bondData.settingsBox.couponFrequency,
      this.bondData.settingsBox.dayCountConvention,
      this.bondData.settingsBox.calculationMethod,
    );

    this.bondService.simulateCalculation(paramsToCalculate)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.markForCheck();
        })
      ).subscribe((data: ResultBox) => {
        const currentResult = JSON.parse(JSON.stringify(this.bondData.resultBox));

        if (data) {
          currentResult.expectedYield = data.expectedYield;
          currentResult.grossYield = data.grossYield;
          currentResult.netYield = data.netYield;
          currentResult.accruedInterest = data.accruedInterest;
          currentResult.basisPointValue = data.basisPointValue;
          currentResult.convexity = data.convexity;
          currentResult.maturity = data.maturity;
          currentResult.accruedInterestDays = data.accruedInterestDays;
          currentResult.totalAccurateInterest = data.totalAccurateInterest;
          currentResult.cashflowItem = data.cashflowItem;
        }

        this.bondData.resultBox = currentResult;
        this.bondData.calcBox.cleanPrice = data.cleanPrice;
        this.bondData.calcBox.expectedYield = data.expectedYield;
        this.bondData = Object.assign({}, this.bondData);
      }, (e: Error) => {
        console.error('Bond - Calcualtor - Widget - calcResult', e);
        this.noDataFound = true;
      });
  }

  instrumentChanged(instrumentId: number) {
    this.bondData.settingsBox = { ... this.defaultSettings };
    this.bondData.calcBox = { ... this.defaultCalcBox };
    this.isLoading = true;
    this.noDataFound = false;
    this.instrumentId = instrumentId;

    this.bondService.getData(instrumentId, new DataArgs(
      this.bondData.settingsBox.valueDate,
      this.bondData.calcBox.expectedYield,
      typeof this.bondData.settingsBox.taxRate === 'string'
        ? parseFloat(this.bondData.settingsBox.taxRate) : this.bondData.settingsBox.taxRate,
      typeof this.bondData.settingsBox.nominalVolume === 'string'
        ? parseFloat(this.bondData.settingsBox.nominalVolume) : this.bondData.settingsBox.nominalVolume,
      this.bondData.settingsBox.dayCountConvention,
      this.bondData.settingsBox.calculationMethod
    )).pipe(
      takeUntil(this.ngUnsubscribe),
      finalize(() => {
        this.isLoading = false;
        this.cdRef.markForCheck();
      })
    ).subscribe((data: BondData) => {

      const mergedSettings: any = {};

      if (data.settingsBox) {
        for (const attrname in data.settingsBox) {
          if (data.settingsBox[attrname]) {
            mergedSettings[attrname] = data.settingsBox[attrname];
          } else {
            mergedSettings[attrname] = this.bondData.settingsBox[attrname];
          }
        }
      }

      this.bondData.settingsBox = Object.assign(this.bondData.settingsBox, mergedSettings);
      this.bondData.subheaderBox = data.subheaderBox;
      this.bondData.resultBox = data.resultBox;
      this.bondData.calcBox = data.resultBox;

      this.bondData = Object.assign({}, this.bondData);

      this.subheaderObj = data.subheaderBox;
      this.instrumentId = instrumentId;

    }, (e: Error) => {
      console.error('Bond - Calcualtor - Widget - instrumentChanged', e);
      this.noDataFound = true;
    });
  }

  private initDefault() {
    this.defaultResultBox = new ResultBox(0, 0, 0, 0, 0, 0, 0, '', '', 0, 0, 0, 0, 0, 0, 0, 0, [], 0, 0, null, true);
    this.defaultCalcBox = new CalcBox(5, 100);
    this.defaultSettings = new SettingsBox(
      3,
      new Date(moment().format('YYYY-MM-DD')),
      new Date(moment().add(1, 'years').format('YYYY-MM-DD')),
      new Date(moment().add(2, 'years').format('YYYY-MM-DD')),
      new Date(moment().add(3, 'years').format('YYYY-MM-DD')),
      100,
      1,
      [new VwdOption(moment().add(2, 'years').format('DD.MM.YYYY'), moment().add(2, 'years').format('YYYY-MM-DD'))],
      new Date(moment().format('YYYY-MM-DD')),
      'isma',
      25,
      100000,
      'act_act',
      true
    );

    this.bondData = new BondData(this.defaultSettings, this.defaultResultBox, null, this.defaultCalcBox);
  }

  // print() {
  //   window.print();
  // }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
